import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ALL_APPS, ALL_VIEWS } from 'constants/appConstants';
import { DASHBOARD_ROUTE } from 'routes/constants';

export const useCurrentApp = () => {
  const location = useLocation();

  const result = useMemo(() => {
    const pathname = location.pathname;

    // Handle special routes first
    if (pathname === '/' || pathname === DASHBOARD_ROUTE) {
      return { currentApp: { name: 'Dashboard' }, currentView: null };
    }

    // First, check if the current path matches a view directly
    const directViewMatch = Object.values(ALL_VIEWS).find(
      (view) => view.path === pathname || view.deprecatedPath === pathname
    );

    if (directViewMatch) {
      // Find the parent app for this view
      const parentApp = Object.values(ALL_APPS).find(
        (app) =>
          app.views &&
          Object.values(app.views).some(
            (view) => view.id === directViewMatch.id
          )
      );

      return {
        currentApp: parentApp || { name: undefined },
        currentView: directViewMatch,
      };
    }

    // Find the app that matches the current path
    // Check for exact matches first, then partial matches
    const exactMatch = Object.values(ALL_APPS).find(
      (app) => app.path === pathname || app.deprecatedPath === pathname
    );

    if (exactMatch) {
      return { currentApp: exactMatch, currentView: null };
    }

    // Check for partial matches (app path is a prefix of current path)
    // Sort by path length to prioritize longer/more specific matches
    const partialMatches = Object.values(ALL_APPS)
      .filter((app) => {
        if (app.path && pathname.startsWith(app.path)) {
          return true;
        }
        // Check deprecated paths as well
        if (app.deprecatedPath && pathname.startsWith(app.deprecatedPath)) {
          return true;
        }
        return false;
      })
      .sort((a, b) => {
        const aPath = a.path || a.deprecatedPath || '';
        const bPath = b.path || b.deprecatedPath || '';
        return bPath.length - aPath.length; // Longer paths first
      });

    const matchedApp = partialMatches[0];

    if (matchedApp) {
      // Check if there's a view within this app that matches the current path
      let matchedView = null;
      if (matchedApp.views) {
        matchedView = Object.values(matchedApp.views).find(
          (view) => view.path && pathname.startsWith(view.path)
        );
      }

      return { currentApp: matchedApp, currentView: matchedView };
    }

    return { currentApp: null, currentView: null };
  }, [location.pathname]);

  return result;
};
